import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Star, ArrowRight, MousePointerClick, Sparkles, Wifi, Smartphone, Laptop, ExternalLink } from "lucide-react";
import { scrollToId } from "@/components/Header";
import { fmtCount } from "@/lib/format";

const LINES = [
  { text: "Get paid for", accent: false },
  { text: "the internet", accent: true },
  { text: "you don't use.", accent: false },
];

const STATS = [
  { icon: ShieldCheck, label: "GDPR-compliant & encrypted", testid: "hero-stat-trust" },
  { icon: Star, label: "4.5/5 average rating", href: "https://www.trustpilot.com/review/honeygain.com", testid: "hero-stat-rating" },
];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.95, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HeroSection = ({ clicks, onJoin }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative overflow-hidden honey-grain pt-32 pb-20 sm:pt-40 sm:pb-28">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 w-[42rem] h-[42rem] rounded-full bg-[radial-gradient(circle,rgba(255,184,0,0.28),transparent_65%)]"
      />
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-8 sm:gap-10 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="hero-bonus-badge"
            className="inline-flex items-center gap-2 rounded-full bg-amber-100 border border-amber-300 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-amber-800"
          >
            <Sparkles className="w-3.5 h-3.5" />
            $3 instant sign-up bonus inside
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-display font-extrabold tracking-tight text-zinc-900 leading-[1.05] text-4xl sm:text-5xl lg:text-6xl"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className={`block ${line.accent ? "italic text-amber-500" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-6 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl"
          >
            Honeygain turns your unused internet bandwidth into real money. Install it once, let it
            run quietly in the background, and cash out — plus grab a <strong className="text-zinc-900">$3 welcome bonus</strong> when
            you join through this link.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9"
          >
            <div className="flex flex-wrap items-center gap-4">
              <motion.button
                data-testid="hero-affiliate-join-button"
                onClick={() => onJoin("hero")}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="group inline-flex items-center gap-2.5 rounded-full bg-amber-400 px-8 py-4 font-display text-base font-bold text-zinc-900 shadow-[0_12px_30px_-10px_rgba(245,158,11,0.6)] hover:bg-amber-300 transition-colors"
              >
                Claim Your $3 Bonus
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </motion.button>
              <span
                data-testid="hero-click-counter"
                className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-700"
              >
                <MousePointerClick className="w-4 h-4 text-amber-600" />
                {fmtCount(clicks)} joined through this page
              </span>
            </div>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("how-it-works")}
              className="mt-4 text-sm font-semibold text-zinc-500 underline underline-offset-4 decoration-zinc-300 hover:text-amber-700 hover:decoration-amber-400 transition-colors"
            >
              See how it works first
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
          >
            {STATS.map(({ icon: Icon, label, href, testid }) =>
              href ? (
                <a
                  key={label}
                  data-testid={testid}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-amber-700 transition-colors"
                >
                  <Icon className="w-5 h-5 text-amber-500" />
                  {label}
                  <ExternalLink className="w-3 h-3 text-zinc-400" />
                </a>
              ) : (
                <span key={label} data-testid={testid} className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600">
                  <Icon className="w-5 h-5 text-amber-500" />
                  {label}
                </span>
              )
            )}
          </motion.div>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: cardY }}
            className="relative rounded-[2.5rem] rounded-tr-[8rem] overflow-hidden border-8 border-white shadow-2xl shadow-amber-900/10 bg-zinc-900 p-6 sm:p-8 min-h-[24rem] flex flex-col"
            data-testid="hero-product-visual"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              Illustration — the Honeygain app
            </p>
            <div className="mt-4 flex items-end justify-between">
              <div>
                <p className="text-xs text-zinc-400">Current balance</p>
                <p className="font-display text-4xl font-extrabold text-white">$14.36</p>
              </div>
              <span className="rounded-full bg-amber-400/15 border border-amber-400/30 px-3 py-1 text-[11px] font-bold text-amber-400">
                +$0.42 today
              </span>
            </div>
            <div className="mt-5">
              <div className="flex justify-between text-[11px] font-semibold text-zinc-400 mb-1.5">
                <span>Progress to $20 payout</span>
                <span>72%</span>
              </div>
              <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "72%" }}
                  transition={{ duration: 1.4, delay: 1.1, ease: "easeOut" }}
                  className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-300"
                />
              </div>
            </div>
            <div className="mt-6 space-y-3">
              {[
                { icon: Smartphone, name: "This phone", status: "Sharing bandwidth" },
                { icon: Laptop, name: "Home laptop", status: "Sharing bandwidth" },
              ].map(({ icon: Icon, name, status }) => (
                <div key={name} className="flex items-center gap-3 rounded-2xl bg-zinc-800/70 px-4 py-3">
                  <Icon className="w-4.5 h-4.5 w-5 h-5 text-amber-400" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">{name}</p>
                    <p className="text-[11px] text-zinc-400">{status}</p>
                  </div>
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-auto pt-5 text-[11px] text-zinc-500">
              Cash out via PayPal or crypto once you reach $20 — from $0.50 in JumpTask mode.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="absolute left-2 sm:-left-8 bottom-10 animate-float-soft"
          >
            <div className="rounded-2xl bg-white/90 backdrop-blur-md border border-amber-100 shadow-xl px-5 py-4" data-testid="hero-earnings-card">
              <p className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Reported by Honeygain</p>
              <p className="font-display text-2xl font-extrabold text-zinc-900">~$27/mo</p>
              <p className="text-xs text-zinc-500">average user payout</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="absolute right-2 sm:-right-6 top-8 animate-float-soft"
            style={{ animationDelay: "1.2s" }}
          >
            <div className="rounded-2xl bg-zinc-900 text-white shadow-xl px-5 py-3.5 flex items-center gap-3" data-testid="hero-status-card">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
              </span>
              <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                <Wifi className="w-4 h-4" /> Sharing bandwidth
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
